import { RADIUS, READING_MEASURE } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { LegalParagraph, LegalProse } from "~/components/legal/LegalProse";
import type { LegalBlock, LegalDocument, LegalSection } from "~/content/legal";

/**
 * Counsel's documents, laid out to be read.
 *
 * A contract is the one screen in the app where nothing may be summarised, folded away
 * or paraphrased, so the whole text is on the page in order — no accordions, no "read
 * more". What the layout is for is making it *navigable*: numbered sections, clause
 * numbers set apart, and a measure narrow enough that a member can follow a line of
 * legal prose on a phone.
 */

/** The processing table, on a screen too narrow for four columns. */
function TableCards({
  columns,
  rows,
}: {
  columns: string[];
  rows: string[][];
}) {
  return (
    <Box
      data-testid="legal.table-cards"
      sx={{
        display: { xs: "flex", md: "none" },
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      {rows.map((row, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            backgroundColor: "surface.inset",
            borderRadius: `${RADIUS.md}px`,
            padding: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          {row.map((cell, cellIndex) => (
            <Box key={cellIndex}>
              <Typography
                variant="L3"
                sx={{
                  color: "text.muted",
                  display: "block",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                {columns[cellIndex]}
              </Typography>
              <Typography
                variant="T3"
                sx={{
                  color: cellIndex === 0 ? "text.primary" : "text.secondary",
                  marginTop: 0.25,
                }}
              >
                <LegalProse>{cell}</LegalProse>
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

/**
 * The same table as a table, once there is room for one.
 *
 * Both renderings are in the DOM and CSS picks between them — the breakpoint is a
 * media query rather than a `useMediaQuery` branch, which would flicker on first paint.
 * The narrow one is a list of labelled cards because a four-column grid on a phone
 * either scrolls sideways or squeezes every column down to two words.
 */
function TableGrid({ columns, rows }: { columns: string[]; rows: string[][] }) {
  return (
    <Box
      component="table"
      data-testid="legal.table"
      sx={{
        display: { xs: "none", md: "table" },
        width: "100%",
        borderCollapse: "collapse",
        tableLayout: "fixed",
      }}
    >
      <Box component="thead">
        <Box component="tr">
          {columns.map((column) => (
            <Box
              key={column}
              component="th"
              scope="col"
              sx={{
                textAlign: "start",
                verticalAlign: "bottom",
                padding: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="L2" sx={{ color: "text.primary" }}>
                {column}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {rows.map((row, rowIndex) => (
          <Box component="tr" key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <Box
                key={cellIndex}
                component="td"
                sx={{
                  verticalAlign: "top",
                  padding: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="T3"
                  sx={{
                    color: cellIndex === 0 ? "text.primary" : "text.secondary",
                  }}
                >
                  <LegalProse>{cell}</LegalProse>
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function Block({ block }: { block: LegalBlock }) {
  if (block.kind === "paragraph") return <LegalParagraph text={block.text} />;

  if (block.kind === "table") {
    return (
      <>
        <TableCards columns={block.columns} rows={block.rows} />
        <TableGrid columns={block.columns} rows={block.rows} />
      </>
    );
  }

  return (
    <Box
      component={block.kind === "steps" ? "ol" : "ul"}
      sx={{
        margin: 0,
        paddingLeft: 3,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {block.items.map((item, index) => (
        <Typography
          key={index}
          component="li"
          variant="T2"
          sx={{ color: "text.secondary" }}
        >
          <LegalProse>{item}</LegalProse>
        </Typography>
      ))}
    </Box>
  );
}

function Section({ section }: { section: LegalSection }) {
  return (
    <Box
      component="section"
      sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
    >
      {section.heading ? (
        <Typography
          variant="H3"
          sx={{ color: "text.primary", maxWidth: READING_MEASURE }}
        >
          {section.number ? (
            <>
              <Box
                component="span"
                sx={{
                  color: "text.muted",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {section.number}
              </Box>{" "}
            </>
          ) : null}
          {section.heading}
        </Typography>
      ) : null}
      {section.blocks.map((block, index) => (
        <Box
          key={index}
          // Prose is held to a measure it can be read at; the processing table needs
          // every pixel of the column it is given.
          sx={{ maxWidth: block.kind === "table" ? "none" : READING_MEASURE }}
        >
          <Block block={block} />
        </Box>
      ))}
    </Box>
  );
}

export function LegalDocumentView({
  document: doc,
}: {
  document: LegalDocument;
}) {
  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: { xs: 3.5, md: 4 } }}
    >
      {doc.sections.map((section, index) => (
        <Section key={index} section={section} />
      ))}
    </Box>
  );
}
